import { z } from "zod";
// Response schemas are tolerant (z.object strips unknown keys): an API
// deployed before a client may add fields, and the client must keep working.
// Request and input schemas stay strict against mass assignment.
export const ErrorCodeSchema = z.enum([
    "conflict",
    "device_revoked",
    "entitlement_read_only",
    "forbidden",
    "idempotency_mismatch",
    "internal",
    "job_enqueue_failed",
    "last_admin",
    "not_found",
    "not_a_member",
    "org_header_missing",
    "pin_ambiguous",
    "rate_limited",
    "revision_conflict",
    "unauthenticated",
    "validation_failed",
]);
export const ErrorDetailSchema = z.object({
    field: z.string(),
    message: z.string(),
});
// Only on 409 pin_ambiguous: the people on the device whose PIN matched.
export const PinMatchSchema = z.object({
    staff_profile_id: z.uuid(),
    display_name: z.string(),
});
export const ErrorEnvelopeSchema = z.object({
    error: z.object({
        code: ErrorCodeSchema,
        message: z.string(),
        field: z.string().optional(),
        request_id: z.string(),
        details: z.array(ErrorDetailSchema).optional(),
        matches: z.array(PinMatchSchema).optional(),
    }),
});
export const HealthResponseSchema = z.object({
    status: z.literal("ok"),
    service: z.literal("safeplate-api"),
});
export const MobileClientSchema = z.enum(["ios", "android"]);
export const MembershipRoleSchema = z.enum(["admin", "manager", "staff"]);
export const IdempotencyKeySchema = z.uuid();
export const MeResponseSchema = z.object({
    session: z.object({
        auth_method: z.enum(["primary", "shared_pin"]),
        scope_org_id: z.uuid().nullable(),
        shared_device_id: z.uuid().nullable(),
    }),
    user: z.object({
        id: z.uuid(),
        email: z.email(),
        name: z.string(),
        image: z.string().nullable(),
    }),
    memberships: z.array(z.object({
        org_id: z.uuid(),
        org_name: z.string(),
        role: MembershipRoleSchema,
        location_id: z.uuid().nullable(),
    })),
    staff_profiles: z.array(z.object({
        org_id: z.uuid(),
        staff_profile_id: z.uuid(),
    })),
});
const postalAddressFields = {
    line1: z.string().optional(),
    line2: z.string().optional(),
    suburb: z.string().optional(),
    city: z.string().optional(),
    postcode: z.string().optional(),
};
export const PostalAddressSchema = z.strictObject(postalAddressFields);
export const PostalAddressResponseSchema = z.object(postalAddressFields);
const organisationMutableFields = {
    legal_name: z.string().refine((value) => value.trim().length > 0, {
        message: "Legal name is required",
    }),
    trading_name: z.string().nullable(),
    nzbn: z.string().nullable(),
    billing_email: z.email().nullable(),
    phone: z.string().nullable(),
    postal_address: PostalAddressSchema.nullable(),
};
export const OrganisationResponseSchema = z.object({
    id: z.uuid(),
    ...organisationMutableFields,
    postal_address: PostalAddressResponseSchema.nullable(),
});
export const OrganisationCreateRequestSchema = z.strictObject({
    legal_name: organisationMutableFields.legal_name,
    trading_name: organisationMutableFields.trading_name.optional(),
    nzbn: organisationMutableFields.nzbn.optional(),
    billing_email: organisationMutableFields.billing_email.optional(),
    phone: organisationMutableFields.phone.optional(),
    postal_address: organisationMutableFields.postal_address.optional(),
});
export const OrganisationUpdateRequestSchema = z
    .strictObject({
    legal_name: organisationMutableFields.legal_name.optional(),
    trading_name: organisationMutableFields.trading_name.optional(),
    nzbn: organisationMutableFields.nzbn.optional(),
    billing_email: organisationMutableFields.billing_email.optional(),
    phone: organisationMutableFields.phone.optional(),
    postal_address: organisationMutableFields.postal_address.optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: "At least one organisation field is required",
});
export function isIanaTimeZone(value) {
    try {
        new Intl.DateTimeFormat("en-NZ", { timeZone: value }).format();
        return true;
    }
    catch {
        return false;
    }
}
export const IanaTimeZoneSchema = z.string().refine(isIanaTimeZone, {
    message: "Timezone must be a valid IANA timezone",
});
export const LocationStatusSchema = z.enum(["setup", "active", "archived"]);
export const EntitlementStateSchema = z.enum([
    "none",
    "trialing",
    "active",
    "past_due",
    "grace",
    "read_only",
    "cancelled",
]);
const locationMutableFields = {
    name: z.string().refine((value) => value.trim().length > 0, {
        message: "Location name is required",
    }),
    trading_name: z.string().nullable(),
    address: PostalAddressSchema,
    council_area: z.string().nullable(),
    timezone: IanaTimeZoneSchema,
    phone: z.string().nullable(),
    email: z.email().nullable(),
    licensable: z.boolean(),
    status: LocationStatusSchema,
};
export const LocationPlanStatusSchema = z.enum([
    "none",
    "needs_review",
    "reviewed",
]);
export const LocationResponseSchema = z.object({
    id: z.uuid(),
    ...locationMutableFields,
    address: PostalAddressResponseSchema,
    entitlement_state: EntitlementStateSchema,
    // Optional so a web build that knows it can run against an API that does
    // not send it yet; the API always sends it.
    plan_status: LocationPlanStatusSchema.optional(),
    // The latest verification visit covering the location (local date), for a
    // "Since last verification" report period. Optional for the same reason.
    last_verified_on: z.iso.date().nullable().optional(),
});
export const LocationCreateRequestSchema = z.strictObject({
    name: locationMutableFields.name,
    trading_name: locationMutableFields.trading_name.optional(),
    address: locationMutableFields.address.default({}),
    council_area: locationMutableFields.council_area.optional(),
    timezone: locationMutableFields.timezone.default("Pacific/Auckland"),
    phone: locationMutableFields.phone.optional(),
    email: locationMutableFields.email.optional(),
    licensable: locationMutableFields.licensable.default(true),
    status: z.enum(["setup", "active"]).default("active"),
});
export const LocationUpdateRequestSchema = z
    .strictObject({
    name: locationMutableFields.name.optional(),
    trading_name: locationMutableFields.trading_name.optional(),
    address: locationMutableFields.address.optional(),
    council_area: locationMutableFields.council_area.optional(),
    timezone: locationMutableFields.timezone.optional(),
    phone: locationMutableFields.phone.optional(),
    email: locationMutableFields.email.optional(),
    licensable: locationMutableFields.licensable.optional(),
    status: locationMutableFields.status.optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: "At least one location field is required",
});
export const LocationIdParamsSchema = z.strictObject({ id: z.uuid() });
export const LatestReadingSchema = z.object({
    asset_id: z.uuid(),
    record_id: z.uuid(),
    kind: z.enum(["chill_air", "chill_food", "freezer", "hot_hold", "display"]),
    value_c: z.number(),
    occurred_at: z.iso.datetime(),
    overall_result: z.enum(["pass", "warn", "fail", "na"]).nullable(),
    performed_by_staff_id: z.uuid(),
    performed_by_name: z.string(),
});
export const LatestReadingsResponseSchema = z.object({
    items: z.array(LatestReadingSchema),
});
export const SocialProviderSchema = z.enum(["google", "apple"]);
export const SocialSignInRequestSchema = z.strictObject({
    provider: SocialProviderSchema,
    id_token: z.string().min(1).optional(),
});
export const RefreshSessionRequestSchema = z.strictObject({
    refresh_token: z.string().min(32),
});
export const MobileTokenPairSchema = z.object({
    access_token: z.string(),
    expires_in: z.number().int().positive(),
    refresh_expires_in: z.number().int().positive(),
    refresh_token: z.string(),
    token_type: z.literal("Bearer"),
});
